import { isStringEqualIgnoreCase } from '../common-utils.js';
import { z } from 'zod';
import { ClassEligibility } from './coach-availabilities.js';
import { lessonSchema } from './lesson.js';

export const learningPathLessonSchema = z.object({
  order: z.number().int(),
  lessonId: z.string(),
  lesson: lessonSchema,
});
export type LearningPathLesson = z.infer<typeof learningPathLessonSchema>;

export const learningPathSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type LearningPath = z.infer<typeof learningPathSchema>;

export const learningPathWithLessonsSchema = learningPathSchema.extend({
  learningPathLessons: z.array(learningPathLessonSchema),
});
export type LearningPathWithLessons = z.infer<typeof learningPathWithLessonsSchema>;

export const createLearningPathOptionsSchema = z.object({
  name: z.string(),
  lessons: z.array(
    z.object({
      lessonId: z.string(),
      order: z.number().int(),
    }),
  ),
});
export type CreateLearningPathOptions = z.infer<typeof createLearningPathOptionsSchema>;

export const updateLearningPathOptionsSchema = z.object({
  name: z.string(),
  lessons: z.array(
    z.object({
      lessonId: z.string(),
      order: z.number().int(),
    }),
  ),
});

export type UpdateLearningPathOptions = z.infer<typeof updateLearningPathOptionsSchema>;

/** Empty CSV cells parse as ""; missing columns become undefined — coerce both to "". */
const csvString = z
  .string()
  .optional()
  .nullable()
  .transform((val) => val ?? '');

export const createLearningPathCsvRow = z.object({
  learningPathName: z.string().min(1, 'Learning path name is required'),
  lessonName: z.string().min(1, 'Lesson name is required'),
  nextLessonName: csvString,
});
export type CreateLearningPathCsvRow = z.infer<typeof createLearningPathCsvRow>;

export const CreateLearningPathCsvFormKeys = {
  csvFile: 'csvFile',
} as const;

export const GetLearningPathResultsSchema = z.object({
  learningPaths: z.array(learningPathWithLessonsSchema),
});
export type GetLearningPathResults = z.infer<typeof GetLearningPathResultsSchema>;

export const learningPathNamesSchema = z.enum([
  'Regular',
  'Regular - extended',
  'Beginner',
  'Beginner - extended',
  'Middle School',
  'Intermediate',
  'Fluency',
  'Fluency (3)',
  'Fluency (1)',
  'Fluency (2)',
  'Practice (Zoom)',
  'Practice (Google Meet)',
  'Diagnostic',
  'Diagnostic w/ Practice (Zoom)',
  'Diagnostic w/ Practice (Google Meet)',
  'Makeup Diagnostic',
  'Read Aloud',
  'Assigned GLA',
]);
export const LearningPathNames = learningPathNamesSchema.enum;
export type LearningPathNames = z.infer<typeof learningPathNamesSchema>;
export const learningPathNamesArray = learningPathNamesSchema.options;

export const interventionLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames['Middle School'],
  LearningPathNames.Intermediate,
  LearningPathNames['Fluency (3)'],
  LearningPathNames['Fluency (1)'],
  LearningPathNames['Fluency (2)'],
  LearningPathNames['Read Aloud'],
];

/**
 * Learning paths whose student groups should appear in the Admin Teacher Attendance
 * view. Mirrors the "regular" student groups that show up in the teacher portal's
 * My Student Groups section (see AIR-1813 / AIR-1868). Practice, diagnostic,
 * fluency, read aloud, and assigned GLA classes are intentionally excluded.
 */
export const attendanceTrackedLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames['Middle School'],
  LearningPathNames.Intermediate,
];

export const practiceLearningPathNames: string[] = [
  LearningPathNames['Practice (Google Meet)'],
  LearningPathNames['Practice (Zoom)'],
];

export const diagnosticLearningPathNames: string[] = [
  LearningPathNames.Diagnostic,
  LearningPathNames['Diagnostic w/ Practice (Google Meet)'],
  LearningPathNames['Diagnostic w/ Practice (Zoom)'],
  LearningPathNames['Makeup Diagnostic'],
];

/** When the most recent student group uses one of these paths, end lesson is the last non-cancelled lesson before them. */
/** Learning path names used by the admin no-future-sessions roster past-session filter. */
export const noFutureSessionsLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames['Middle School'],
  LearningPathNames.Fluency,
  LearningPathNames['Makeup Diagnostic'],
  LearningPathNames['Read Aloud'],
  'Practice',
  LearningPathNames.Diagnostic,
  'Diagnostic w/ Practice',
];

export const endLessonFluencyLookbackLearningPathNames: string[] = [
  LearningPathNames.Fluency,
  LearningPathNames['Fluency (1)'],
  LearningPathNames['Fluency (2)'],
  LearningPathNames['Fluency (3)'],
  LearningPathNames['Read Aloud'],
];

export const returningStudentFluencyRemapSourcePathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames.Intermediate,
  LearningPathNames['Middle School'],
];

export const studentGroupTypeSchema = z.enum(['one_time', 'recurring']);
export type StudentGroupType = z.infer<typeof studentGroupTypeSchema>;
export const StudentGroupType = studentGroupTypeSchema.enum;
export const StudentGroupTypeLabelMap: Record<StudentGroupType, string> = {
  [StudentGroupType.one_time]: 'One-time',
  [StudentGroupType.recurring]: 'Recurring',
};

/** Learning paths for student groups shown under the admin Type filter → One-time. */
export const oneTimeStudentGroupLearningPathNames: string[] = [
  LearningPathNames['Practice (Google Meet)'],
  LearningPathNames['Practice (Zoom)'],
  LearningPathNames.Diagnostic,
  LearningPathNames['Diagnostic w/ Practice (Google Meet)'],
  LearningPathNames['Diagnostic w/ Practice (Zoom)'],
];

/** Learning paths for student groups shown under the admin Type filter → Recurring. */
export const recurringStudentGroupLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames.Fluency,
  LearningPathNames['Fluency (1)'],
  LearningPathNames['Fluency (2)'],
  LearningPathNames['Fluency (3)'],
  LearningPathNames.Intermediate,
  LearningPathNames['Makeup Diagnostic'],
  LearningPathNames['Middle School'],
  LearningPathNames['Read Aloud'],
];

export function learningPathNamesForStudentGroupTypes(types: StudentGroupType[]): string[] {
  const names = new Set<string>();

  for (const type of types) {
    if (type === StudentGroupType.one_time) {
      for (const name of oneTimeStudentGroupLearningPathNames) {
        names.add(name);
      }
    } else if (type === StudentGroupType.recurring) {
      for (const name of recurringStudentGroupLearningPathNames) {
        names.add(name);
      }
    }
  }

  return [...names];
}

/**
 * Class eligibility tags required for a coach to teach sessions under this learning path.
 * Used when filtering "Available teachers" (admin class edit, bulk update, sub teachers).
 */
export function classEligibilitiesRequiredForLearningPathName(name: string): ClassEligibility[] {
  if (interventionLearningPathNames.some((learningPathName) => isStringEqualIgnoreCase(learningPathName, name))) {
    return [ClassEligibility.can_teach_intervention];
  }

  if (practiceLearningPathNames.some((learningPathName) => isStringEqualIgnoreCase(learningPathName, name))) {
    return [ClassEligibility.can_teach_practice];
  }

  if (diagnosticLearningPathNames.some((learningPathName) => isStringEqualIgnoreCase(learningPathName, name))) {
    return [ClassEligibility.can_teach_diagnostic];
  }

  return [ClassEligibility.can_teach_intervention];
}

/**
 * Union of class eligibility tags required across multiple learning paths (e.g. bulk teacher update).
 * When several paths are selected, a coach must have every returned tag to appear as available
 * (server filters with `classEligibilities: { hasEvery: [...] }`).
 */
export function classEligibilitiesRequiredForLearningPathNames(names: string[]): ClassEligibility[] {
  const uniqueNames = [...new Set(names)];
  if (uniqueNames.length === 0) {
    return classEligibilitiesRequiredForLearningPathName('');
  }
  return [...new Set(uniqueNames.flatMap(classEligibilitiesRequiredForLearningPathName))];
}
